import { filter, find, isEmpty, maxBy, size } from "lodash";
import {
  AICC_ASSET_STATUS,
  AICC_AUDIENCE,
  AICC_LANGUAGE_ACTION,
  AICC_LANGUAGE_STATUS,
  AICC_SUMMARY_STATE,
  AICC_VERSION_ROW,
  AICC_WORKING_COPY_ORIGIN_LABEL,
  AICC_VERSION_SCOPE,
  AICC_VERSION_STATUS,
  AiccLanguageAction,
  AiccLanguageStatus,
  AiccVersionRowKind,
  AiccVersionScope,
} from "constants/aiccLanguage";
import {
  AiccLanguageAsset,
  AiccLanguageContentVersion,
  AiccLanguageVersion,
  AiccManagedLanguage,
} from "types/model/aiccLanguage";

/**
 * What the language list needs to ask about a language, as pure functions. The lifecycle itself is
 * server-side (AICC2-495) — nothing here simulates a transition:
 *
 *   draft ──(all 14 assets marked)──▶ preview-ready ──[Ready to Publish]──▶ publish-ready
 *         ──[Public to All User]──▶ public                    ▲ any edit releases the lock
 *
 * Two versions coexist while a language is revised (AICC2-331): the live one keeps serving end users
 * while Duplicate Preview clones it into a working copy only test accounts see.
 */

function isAssetReady(asset: AiccLanguageAsset): boolean {
  return asset.status !== AICC_ASSET_STATUS.draft;
}

/**
 * The backend's `public_version` is the field every other answer is derived from server-side, so it is
 * read directly; the history is a fallback for responses older than the field.
 */
export function liveVersionNumber(language: AiccManagedLanguage): number | undefined {
  return language.liveVersion ?? find(language.versions, { status: AICC_VERSION_STATUS.public })?.version;
}

/**
 * Either signal is enough. Dev has a language published before releases were recorded, so it serves a
 * version its history does not mention — and for "is this live" the safe reading of a disagreement is
 * yes.
 */
export function isServingEndUsers(language: AiccManagedLanguage): boolean {
  return language.isPublic || !!liveVersionNumber(language);
}

/** The way into the history: a withdrawn language has no live version, and restoring is its only way back. */
export function latestVersion(language: AiccManagedLanguage): AiccLanguageVersion | undefined {
  return maxBy(language.versions, "version");
}

/**
 * The server's flag, never inferred from the status: withdrawing leaves a language `publish-ready`
 * with no copy at all, and edits without one are refused with 409.
 */
export function hasWorkingCopy(language: AiccManagedLanguage): boolean {
  return language.hasWorkingCopy;
}

/**
 * The number publishing would land on, which the publish dialog and the working row both show.
 *
 * `next_version` wins because the rule is not derivable from the history: withdrawing with no copy in
 * flight leaves the version staged, and republishing it unedited keeps that number rather than adding
 * an indistinguishable successor. The derivation below reproduces that rule for older responses.
 */
export function workingCopyVersionNumber(language: AiccManagedLanguage): number {
  if (language.nextVersion) {
    return language.nextVersion;
  }

  const staged = find(language.versions, { status: AICC_VERSION_STATUS.publishReady });
  if (staged) {
    return staged.version;
  }

  const live = liveVersionNumber(language);
  if (live) {
    return live + 1;
  }

  const highest = maxBy(language.versions, "version");
  return highest ? highest.version + 1 : 1;
}

export interface AiccAssetReadiness {
  /** Required assets only, so the count can never read "not ready" against a Preview-Ready dot. */
  total: number;
  ready: number;
  blocked: number;
}

/**
 * How much of a copy is done, for its row to show while collapsed.
 *
 * Takes the assets rather than the language, so each row counts its own copy — the same fourteen read
 * `published` on a frozen version and `draft` on the editable one.
 */
export function assetReadiness(assets: AiccLanguageAsset[]): AiccAssetReadiness {
  const requiredAssets = filter(assets, "isRequired");
  const total = size(requiredAssets);
  const ready = size(filter(requiredAssets, isAssetReady));

  return { total, ready, blocked: total - ready };
}

/** One version of a language, as the card renders it. */
export interface AiccLanguageVersionRow {
  kind: AiccVersionRowKind;
  key: string;
  versionLabel: string;
  status: AiccLanguageStatus;
  audience: string;
  actions: AiccLanguageAction[];
  assets: AiccLanguageAsset[];
  /**
   * Whether this row is the working copy — the one row a write could land on.
   *
   * 🔴 CURRENTLY NOTHING READS IT. Its only consumer was the sub-asset table's Mark Preview-Ready
   * action, which the BA dropped; the answer itself is still correct and the lifecycle above is what
   * makes it so, which is why it is kept rather than deleted along with the button.
   */
  isEditable: boolean;
  /** Working copies only — a frozen version has nothing left to complete. */
  readiness?: AiccAssetReadiness;
  /** Working copies only: where this copy came from, e.g. "restored from V3". */
  origin?: string;
}

/**
 * The shape of the whole card: a language is not one thing with a shifting status, it is one or two
 * versions each with its own state, audience and actions, and each row only ever describes itself.
 *
 *   never published           → one working row, numbered as the version it would become
 *   published, nobody editing → one live row
 *   published + being revised → both
 *   withdrawn                 → one withdrawn row, which can go back out unchanged
 *
 * The copies are a parameter because a card is expanded before they exist: the list is read without
 * them and they arrive per language on expand.
 *
 * Delete is absent by design — it removes the language, not a version (see `languageMenuActions`).
 */
export function languageVersionRows(
  language: AiccManagedLanguage,
  contentVersions: AiccLanguageContentVersion[]
): AiccLanguageVersionRow[] {
  if (language.status === AICC_LANGUAGE_STATUS.archived) {
    return [];
  }

  const contentVersion = (scope: AiccVersionScope) => find(contentVersions, { scope });
  const live = contentVersion(AICC_VERSION_SCOPE.public);
  const rows: AiccLanguageVersionRow[] = [];

  if (live) {
    rows.push({
      kind: AICC_VERSION_ROW.live,
      key: `live-${live.versionNumber ?? "current"}`,
      versionLabel: `V${live.versionNumber ?? liveVersionNumber(language)}`,
      status: AICC_LANGUAGE_STATUS.public,
      audience: AICC_AUDIENCE.allUsers,
      // Duplicate Preview stays offered while a copy exists: AICC2-331 AC-5 makes it "V1 overwrite
      // V2", and its dialog is where the copy being discarded is spelled out. It is the only action a
      // live version has — taking one away from end users is no longer something this screen does.
      actions: [AICC_LANGUAGE_ACTION.duplicatePreview],
      assets: live.assets,
      isEditable: false,
    });
  }

  if (hasWorkingCopy(language)) {
    // Empty until the copies arrive; borrowing another scope's would put the wrong statuses here.
    const workingAssets = contentVersion(AICC_VERSION_SCOPE.working)?.assets ?? [];

    rows.push({
      kind: AICC_VERSION_ROW.working,
      key: "working",
      versionLabel: `V${workingCopyVersionNumber(language)}`,
      status: language.status,
      audience: language.status === AICC_LANGUAGE_STATUS.draft ? AICC_AUDIENCE.nobody : AICC_AUDIENCE.testAccounts,
      actions: workingCopyActions(language.status),
      assets: workingAssets,
      isEditable: true,
      readiness: assetReadiness(workingAssets),
      origin: workingCopyOrigin(language),
    });

    return rows;
  }

  if (!isEmpty(rows)) {
    return rows;
  }

  // Neither copy: a published version that was withdrawn, still publish-ready so it can go back out
  // as itself.
  //
  // 🔴 STILL RENDERED THOUGH NOTHING HERE CAN PRODUCE IT ANY MORE. Unpublish was taken out of this
  // screen, but the state it left behind is in the data — a language withdrawn before that, or by any
  // other path — and such a language has to remain readable and publishable rather than becoming a
  // blank panel.
  //
  // 🔴 ITS CONTENT IS READABLE, which this row used to deny by hardcoding `assets: []`. Withdrawing
  // moves the pointer and copies nothing, so test accounts are still reading that version — the whole
  // reason the language stays in the list. Dev's French sent fourteen assets at 14/14 while this
  // rendered an empty table and a note saying there was nothing to see.
  //
  // Also the last resort: a language is in the list because there is something to say about it, so no
  // rows at all would be a blank panel rather than an answer.
  return [
    {
      kind: AICC_VERSION_ROW.withdrawn,
      key: "withdrawn",
      versionLabel: `V${workingCopyVersionNumber(language)}`,
      status: language.status,
      audience: AICC_AUDIENCE.testAccounts,
      actions: [AICC_LANGUAGE_ACTION.publicToAllUser],
      assets: contentVersion(AICC_VERSION_SCOPE.withdrawn)?.assets ?? [],
      // A published version is immutable, which is what `WITHDRAWN_ASSETS_NOTE` sends the operator to
      // Restore for.
      isEditable: false,
    },
  ];
}

/**
 * "copied from V5", "restored from V3", or "new language", which has no source to name. Absent when
 * the backend does not say — copies made before this was recorded have none, and inventing one would
 * be worse than silence.
 */
function workingCopyOrigin(language: AiccManagedLanguage): string | undefined {
  const { workingCopyOrigin: origin, workingCopySource: source } = language;
  if (!origin) {
    return undefined;
  }

  const label = AICC_WORKING_COPY_ORIGIN_LABEL[origin];
  return source ? `${label} V${source}` : label;
}

/** Draft advances by finishing assets, not by asking, so it offers nothing. */
function workingCopyActions(status: AiccLanguageStatus): AiccLanguageAction[] {
  if (status === AICC_LANGUAGE_STATUS.previewReady) {
    return [AICC_LANGUAGE_ACTION.readyToPublish];
  }
  if (status === AICC_LANGUAGE_STATUS.publishReady) {
    return [AICC_LANGUAGE_ACTION.publicToAllUser];
  }

  return [];
}

/**
 * Actions belonging to the language rather than to one of its versions.
 *
 * Delete is hidden while a version is live — "(hide Delete)" against every public row of the AICC2-331
 * and AICC2-335 matrices. Taking a language away from end users is what Unpublic is for; Delete doing
 * it as a side effect would make one menu item mean two things.
 */
export function languageMenuActions(language: AiccManagedLanguage): AiccLanguageAction[] {
  if (language.status === AICC_LANGUAGE_STATUS.archived || isServingEndUsers(language)) {
    return [];
  }

  return [AICC_LANGUAGE_ACTION.deleteLanguage];
}

/**
 * What Add Language just did, in words — AICC2-516.
 *
 * 🔴 ONE ENDPOINT, FOUR ACTS, ANNOUNCED THE SAME. `POST /languages/{id}/create` answers 200 whether it
 * added a language, brought one back from the archive, did both, or found nothing to do, and the modal
 * said "Added X successfully." for all four. Reported against Japanese: an operator archived it, added
 * it back, and could not tell from the screen that it had returned carrying its published version, its
 * review marks and its staging decision.
 *
 * READ FROM THE RESPONSE, NEVER DERIVED. `isDeleted` is false in all four and the status can be
 * identical, so the only other route would be remembering why the id was in the Add list — a second
 * copy of a backend rule, in the place least able to keep it in step.
 */
export function addLanguageOutcomeMessage(language: AiccManagedLanguage): string {
  const { name, workingCopyCreated: created, restoredFromArchive: restored } = language;

  // `created` first, so an archived language that had nothing to come back to — un-archived AND given
  // an empty copy, both flags true — is announced as an add. It holds no content, and the restore
  // sentence would promise content, marks and a published version that are not there.
  //
  // Folded in with both fields absent, which is every read that is not a copy action and any env on a
  // backend older than the fields: that case cannot know which act it was, and this sentence is the
  // one true of all four.
  if (created || (created === undefined && restored === undefined)) {
    return `Added ${name} successfully.`;
  }

  if (restored) {
    // Still withdrawn: a restore clears `public_version`, so end users do not have it back until
    // somebody publishes again. A second line rather than a modal, so every branch stays a toast.
    return (
      `Restored ${name} with its existing content, review marks and published version.\n` +
      "It is visible to test accounts only until you publish it again."
    );
  }

  return `${name} is already set up — nothing was changed.`;
}

/** What a summary entry says about one version. */
export interface AiccLanguageSummaryEntry {
  /**
   * Absent for a live version whose number nothing reports. Saying "live" without a number is the
   * honest form of that; borrowing the next version's would be off by one.
   */
  versionLabel?: string;
  state: string;
  /** So the number can be coloured like that version's own dot. */
  status: AiccLanguageStatus;
}

/**
 * The collapsed card's one line, as parts rather than a sentence: the header renders the numbers and
 * the words differently and joins them with an arrow.
 *
 * Live first, so the arrow reads as the replacement in progress. Everything here comes from the list
 * response, so a collapsed card needs no request of its own.
 */
export function languageSummaryEntries(language: AiccManagedLanguage): AiccLanguageSummaryEntry[] {
  const isLive = isServingEndUsers(language);
  const liveNumber = liveVersionNumber(language);
  const entries: AiccLanguageSummaryEntry[] = [];

  if (isLive) {
    entries.push({
      versionLabel: liveNumber ? `V${liveNumber}` : undefined,
      state: AICC_SUMMARY_STATE.live,
      status: AICC_LANGUAGE_STATUS.public,
    });
  }
  if (hasWorkingCopy(language)) {
    entries.push({
      versionLabel: `V${workingCopyVersionNumber(language)}`,
      state: AICC_SUMMARY_STATE.inPreview,
      status: language.status,
    });
  }
  if (!isLive && !hasWorkingCopy(language) && !isEmpty(language.versions)) {
    entries.push({
      versionLabel: `V${workingCopyVersionNumber(language)}`,
      state: AICC_SUMMARY_STATE.withdrawn,
      status: language.status,
    });
  }

  return entries;
}
